import type { DeferredDownPaymentV2 } from "@credit-acceptance/step6-bff-client";
import { useTranslation } from "react-i18next";

import { formatIsoDate } from "../../../../lib/format";
import { formatCurrency, sumAmounts } from "../../../../lib/money";
import type { AuditTableColumn } from "../../../audit/AuditTable";
import SummaryTableModal from "../../components/SummaryTableModal";
import { EMPTY_VALUE, formatOptional } from "./financialFormat";

interface DeferredDownPaymentModalProperties {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  deferredDownPayment: DeferredDownPaymentV2;
  /** `financial.itemization.downPayment.cashDown`, collected today. */
  cashDown?: number;
  /** Date shown for the cash down collected today (the contract date). */
  cashDownDate?: string;
  /** The deal's Total Down Payment, as a positive amount. */
  totalDown?: number;
}

interface CashDownRow {
  key: string;
  description: string;
  amount?: number;
  date?: string;
}

// Read-only cash down breakdown (today plus the deferred installments), opened from the "Cash Down" link
function DeferredDownPaymentModal({
  open,
  onOpenChange,
  deferredDownPayment,
  cashDown,
  cashDownDate,
  totalDown,
}: DeferredDownPaymentModalProperties) {
  const { t } = useTranslation();
  const entries = deferredDownPayment.entries ?? [];

  const rows: CashDownRow[] = [
    ...(cashDown === undefined
      ? []
      : [
          {
            key: "today",
            description: t("financial.deferredDownPaymentModal.cashDownToday"),
            amount: cashDown,
            date: cashDownDate,
          },
        ]),
    ...entries.map((entry, index) => ({
      key: `deferred-${index}`,
      description: t("financial.deferredDownPaymentModal.deferredCashDown", { index: index + 1 }),
      amount: entry.amount,
      date: entry.date,
    })),
  ];

  const columns: Array<AuditTableColumn<CashDownRow>> = [
    { key: "description", header: t("financial.modalColumns.description"), render: (row) => row.description },
    {
      key: "amount",
      header: t("financial.modalColumns.amount"),
      render: (row) => formatOptional(row.amount, formatCurrency),
    },
    {
      key: "date",
      header: t("financial.modalColumns.date"),
      render: (row) => formatIsoDate(row.date) || EMPTY_VALUE,
    },
  ];

  const totalCashDown = sumAmounts(rows.map((row) => ({ amount: row.amount ?? 0 })));

  return (
    <SummaryTableModal
      open={open}
      onOpenChange={onOpenChange}
      title={t("financial.deferredDownPaymentModal.title")}
      description={t("financial.deferredDownPaymentModal.description")}
      caption={t("financial.deferredDownPaymentModal.caption")}
      summaryLines={[
        { label: t("financial.deferredDownPaymentModal.totalCashDown"), value: formatCurrency(totalCashDown) },
        { label: t("financial.deferredDownPaymentModal.totalDown"), value: formatOptional(totalDown, formatCurrency) },
        {
          label: t("financial.deferredDownPaymentModal.maxDeferredDownDate"),
          value: formatIsoDate(deferredDownPayment.maxDeferredDownDate) || EMPTY_VALUE,
        },
      ]}
      columns={columns}
      rows={rows}
    />
  );
}

export default DeferredDownPaymentModal;
